import React, { useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { MONTHS } from "@/constants/scheduleConstant";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { Colors } from "@/constants/theme";

interface ScheduleHeaderProps {
  currentWeekStart: Date;
  onTodayPress: () => void;
  setDate: (date: Date) => void;
}

function getDisplayMonthDate(currentWeekStart: Date): Date {
  const weekStart = new Date(currentWeekStart);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const firstOfNextMonth = new Date(weekStart.getFullYear(), weekStart.getMonth() + 1, 1);

  if (firstOfNextMonth >= weekStart && firstOfNextMonth <= weekEnd) {
    return firstOfNextMonth;
  }

  return weekStart;
}

export default function ScheduleHeader({
  currentWeekStart,
  onTodayPress,
  setDate,
}: Readonly<ScheduleHeaderProps>) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme];
  const { width: windowWidth } = useWindowDimensions();
  const shouldStackControls = Platform.OS === "web" && windowWidth < 640;

  const [monthPickerVisible, setMonthPickerVisible] = useState(false);

  const displayMonthDate = getDisplayMonthDate(currentWeekStart);
  const month = displayMonthDate.getMonth();
  const year = displayMonthDate.getFullYear();

  function handleMonthSelect(selectedMonth: number) {
    const firstOfMonth = new Date(year, selectedMonth, 1); // Locate first day of month
    firstOfMonth.setDate(firstOfMonth.getDate() - firstOfMonth.getDay()); // Rewind to the closest Sunday before this date
    setDate(firstOfMonth);
    setMonthPickerVisible(false);
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.background,
          ...(Platform.OS === "web"
            ? { boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)" }
            : {
              shadowColor: theme.scheduleHeader.shadowColor,
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.06,
              shadowRadius: 4,
            }),
        },
      ]}
    >
      <View style={styles.titleRow}>
        <Text
          style={[
            styles.title,
            shouldStackControls && styles.titleCompact,
            { color: theme.tint },
          ]}
        >
          Class Schedule
        </Text>
      </View>

      <View
        style={[styles.controlsRow, shouldStackControls && styles.controlsRowCompact]}
      >
        <View
          style={[
            styles.monthButtonContainer,
            shouldStackControls && styles.monthButtonContainerCompact,
          ]}
        >
          <Pressable
            onPress={() => setMonthPickerVisible(true)}
            style={[styles.monthButton, shouldStackControls && styles.monthButtonCompact]}
            accessibilityLabel="Change month"
          >
            <Text
              style={[
                styles.monthButtonText,
                { color: theme.scheduleHeader.monthButtonText },
              ]}
            >
              {MONTHS[month]} {year}
            </Text>
            <MaterialIcons
              name="arrow-drop-down"
              size={20}
              color={theme.scheduleHeader.monthButtonText}
            />
          </Pressable>
        </View>

        <View
          style={[
            styles.todayButtonContainer,
            shouldStackControls && styles.todayButtonContainerCompact,
          ]}
        >
          <Pressable
            onPress={onTodayPress}
            style={[
              styles.todayButton,
              shouldStackControls && styles.todayButtonCompact,
              { backgroundColor: theme.icon, borderColor: theme.icon },
            ]}
            accessibilityLabel="Jump to today"
          >
            <Text
              style={[
                styles.todayButtonText,
                { color: theme.scheduleHeader.todayButtonText },
              ]}
            >
              Today
            </Text>
          </Pressable>
        </View>
      </View>

      <Modal
        visible={monthPickerVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setMonthPickerVisible(false)}
      >
        <View
          style={[
            styles.backdrop,
            { backgroundColor: theme.scheduleHeader.monthPickerBackdrop },
          ]}
        >
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setMonthPickerVisible(false)}
          />
          <View
            style={[
              styles.monthMenu,
              {
                backgroundColor: theme.scheduleHeader.monthPickerBackground,
                ...(Platform.OS === "web"
                  ? { boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)" }
                  : {
                    shadowColor: theme.scheduleHeader.shadowColor,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.15,
                    shadowRadius: 12,
                  }),
              },
            ]}
          >
            {MONTHS.map((name, index) => (
              <Pressable
                key={name}
                onPress={() => handleMonthSelect(index)}
                style={[
                  styles.monthMenuItem,
                  index === month && {
                    backgroundColor: theme.scheduleHeader.monthMenuItemActive,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.monthMenuItemText,
                    { color: theme.scheduleHeader.monthMenuText },
                    index === month &&
                    styles.monthMenuItemTextActive && {
                      color: theme.scheduleHeader.monthMenuTextActive,
                    },
                  ]}
                >
                  {name}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 12,
    paddingBottom: 10,
    paddingHorizontal: 16,
    elevation: 2,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center", // centers the title horizontally
    marginBottom: 15,
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    letterSpacing: -0.3,
  },
  titleCompact: {
    fontSize: 21,
  },

  // Row with month picker and today button
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  controlsRowCompact: {
    flexWrap: "wrap",
    justifyContent: "center",
    rowGap: 8,
  },
  monthButtonContainer: {
    flex: 3,
    alignItems: "center",
    paddingRight: 5,
  },
  monthButtonContainerCompact: {
    flexBasis: "100%",
    flexGrow: 0,
    paddingRight: 0,
  },
  monthButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
  },
  monthButtonCompact: {
    alignSelf: "center",
  },
  monthButtonText: {
    fontSize: 15,
    fontWeight: "600",
  },
  todayButtonContainer: {
    flex: 2,
    alignItems: "flex-start",
    paddingLeft: 20,
  },
  todayButtonContainerCompact: {
    flexBasis: "100%",
    flexGrow: 0,
    alignItems: "center",
    paddingLeft: 0,
  },
  todayButton: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  todayButtonCompact: {
    alignSelf: "center",
  },
  todayButtonText: {
    fontSize: 15,
    fontWeight: "600",
  },

  // Month picker
  backdrop: {
    flex: 1,
    justifyContent: "flex-start",
    paddingTop: 100,
    paddingHorizontal: 16,
  },
  monthMenu: {
    borderRadius: 12,
    paddingVertical: 8,
    elevation: 8,
  },
  monthMenuItem: {
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  monthMenuItemText: {
    fontSize: 16,
  },
  monthMenuItemTextActive: {
    fontWeight: "600",
  },
});
